// aiContext.js — per-turn context builders for Hayah AI surfaces.
//
// Each builder pulls only the data slices relevant to the surface it
// serves. The AI Coach uses keyword-based relevance to subset further
// (so a "what's my protein looking like?" question doesn't send
// workout history). Insight surfaces (Coach Report, Today Insights,
// post-workout, weekly recap) include a full structured payload
// because each is a one-shot, not part of a long-running chat.
//
// allData is the result of readAllData() — a single read of every
// relevant localStorage key, captured once per builder invocation so
// derived metrics use a consistent snapshot.

import { getUserProfile } from "./userProfile";
import { getMounjaroDay } from "./mounjaroCycle";
import { computeCardioFitness } from "./cardioFitness";
import { resolveExerciseId, resolveExerciseName } from "../exerciseCanonical";
import { computeFastingWindow, computeCurrentMealWindow, FASTING_PROTOCOLS } from "../hayahPrefs";
import {
  DEFAULT_JUNK_THRESHOLD,
  computeRangeJunkStats,
  computeJunkStreak,
  computeTodayEarnedCheat,
  isCheatClaimedToday,
} from "./junkStats";

// ── helpers ──────────────────────────────────────────────────────────────
function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function dayOffset(n) {
  const d = new Date(); d.setDate(d.getDate() + n);
  return localDateStr(d);
}
function safeJSON(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key) || "null"); return v == null ? fallback : v; }
  catch { return fallback; }
}
function calOf(log) {
  if (!log) return 0;
  return Object.values(log.meals || {}).flat().reduce((s, e) => s + (e?.calories || 0), 0);
}
function protOf(log) {
  if (!log) return 0;
  return Object.values(log.meals || {}).flat().reduce((s, e) => s + (e?.protein || 0), 0);
}
function carbsOf(log) {
  if (!log) return 0;
  return Object.values(log.meals || {}).flat().reduce((s, e) => s + (e?.carbs || 0), 0);
}
function fatOf(log) {
  if (!log) return 0;
  return Object.values(log.meals || {}).flat().reduce((s, e) => s + (e?.fat || 0), 0);
}
function fiberOf(log) {
  if (!log) return 0;
  return Object.values(log.meals || {}).flat().reduce((s, e) => s + (e?.fiber || 0), 0);
}
function avg(vals) {
  const v = (vals || []).filter(x => x != null && Number.isFinite(x));
  return v.length ? +(v.reduce((s, x) => s + x, 0) / v.length).toFixed(2) : null;
}
function workoutTs(w) {
  return w.timestamp || w.completedAt || w.startedAt
    || (w.date ? Date.parse(`${w.date}T12:00:00`) : 0);
}

// Reads every Hayah data store in one pass. Cheap (localStorage is
// synchronous + small) but call once per builder so all derived
// metrics see the same snapshot.
export function readAllData() {
  return {
    today: localDateStr(),
    calorieLog:       safeJSON("calorieLog", {}),
    calorieGoals:     safeJSON("calorieGoals", {}),
    weightLog:        safeJSON("calorieWeightLog", []),
    profile:          safeJSON("ghidhaProfile", {}),
    workouts:         safeJSON("sirat-workouts", []),
    fastingLog:       safeJSON("fastingLog", {}),
    fastingOverrides: safeJSON("fastingOverrides", {}),
    fastEvents:       safeJSON("hayahFastEvents", []),
    prefs:            safeJSON("hayahPrefs", {}),
  };
}

// ── TODAY SLICE ──────────────────────────────────────────────────────────
function todaySlice(allData) {
  const t = allData.calorieLog[allData.today] || {};
  const cals  = Math.round(calOf(t));
  const prot  = Math.round(protOf(t));
  const carbs = Math.round(carbsOf(t));
  const fat   = Math.round(fatOf(t));
  const fiber = Math.round(fiberOf(t));
  const burnA = Math.round(t.caloriesBurned || 0);
  const burnB = Math.round(t.basalBurned || 0);
  const goals = allData.calorieGoals || {};
  // Itemised list of everything logged today (name + slot + macros), so the
  // Coach can answer "what did I eat today?" / reason about specific items
  // (e.g. "should I have a protein shake now?") — not just the totals.
  const foods = [];
  for (const [slot, arr] of Object.entries(t.meals || {})) {
    for (const e of (arr || [])) {
      if (e && e.name) foods.push({
        name: e.name, slot,
        kcal: Math.round(e.calories || 0),
        p: Math.round(e.protein || 0),
        c: Math.round(e.carbs || 0),
        f: Math.round(e.fat || 0),
      });
    }
  }
  return {
    date: allData.today,
    foods: foods.slice(0, 50),
    intake: { calories: cals, protein: prot, carbs, fat, fiber, water: t.water || 0 },
    goals: {
      calories: goals.calories || null,
      protein:  goals.protein  || null,
      carbs:    goals.carbs    || null,
      fat:      goals.fat      || null,
      fiber:    goals.fiber    || null,
    },
    remaining: {
      calories: (goals.calories || 0) - cals,
      protein:  (goals.protein  || 0) - prot,
      carbs:    (goals.carbs    || 0) - carbs,
      fat:      (goals.fat      || 0) - fat,
    },
    activity: {
      steps: t.steps || 0,
      stepsGoal: goals.steps || 10000,
      distanceKm: t.distanceMeters ? +((t.distanceMeters || 0) / 1000).toFixed(2) : 0,
      exerciseMin: t.exerciseMinutes || 0,
      standHours: t.standHours || 0,
      activeBurnKcal: burnA,
      basalBurnKcal: burnB,
      totalBurnKcal: burnA + burnB,
    },
    sleepLastNight: t.sleepMinutes ? {
      totalMin: t.sleepMinutes,
      deepMin: t.deepMinutes || 0,
      remMin:  t.remMinutes || 0,
      awakeMin: t.awakeMinutes || 0,
    } : null,
    vitals: {
      restingHr: t.restingHeartRate || null,
      hrv: t.heartRateVariability || null,
      spo2: t.oxygenSaturation || null,
      respRate: t.respiratoryRate || null,
    },
    body: {
      weightKg:   t.bodyMassKg || null,
      bodyFatPct: t.bodyFatPct || null,
    },
  };
}

// Yesterday's outcome — for delta context in Today insights.
function yesterdaySlice(allData) {
  const y = dayOffset(-1);
  const log = allData.calorieLog[y];
  if (!log) return null;
  const cal = Math.round(calOf(log));
  if (cal === 0 && !log.sleepMinutes && !log.steps) return null;
  return {
    date: y,
    calories: cal,
    protein: Math.round(protOf(log)),
    sleepMin: log.sleepMinutes || null,
    steps: log.steps || null,
    bodyMassKg: log.bodyMassKg || null,
  };
}

// 7-day rolling series — used by Today insights for trend context and
// by buildCoachContext for the recovery/nutrition slices.
function trend7d(allData) {
  const days = Array.from({ length: 7 }, (_, i) => dayOffset(-(6 - i)));
  const series = days.map(d => {
    const l = allData.calorieLog[d];
    return {
      date: d,
      calories:  l ? Math.round(calOf(l)) : null,
      protein:   l ? Math.round(protOf(l)) : null,
      sleepMin:  l?.sleepMinutes || null,
      steps:     l?.steps || null,
      restingHr: l?.restingHeartRate || null,
      hrv:       l?.heartRateVariability || null,
      bodyMassKg: l?.bodyMassKg || null,
      bodyFatPct: l?.bodyFatPct || null,
    };
  });
  return {
    days: series,
    avgCalories:  avg(series.map(s => s.calories)),
    avgProtein:   avg(series.map(s => s.protein)),
    avgSleepMin:  avg(series.map(s => s.sleepMin)),
    avgSteps:     avg(series.map(s => s.steps)),
    avgRestingHr: avg(series.map(s => s.restingHr)),
    avgHrv:       avg(series.map(s => s.hrv)),
  };
}

// ── PHOTO CONTEXT (minimal: today's remaining + halal + profile basics) ──
export function buildPhotoContext(allData) {
  const today = todaySlice(allData);
  const profile = getUserProfile();
  return {
    today: {
      remaining: today.remaining,
      intakeSoFar: today.intake,
      goals: today.goals,
    },
    profile: {
      region: profile.identity.region,
      halalOnly: profile.nutrition.halalOnly,
      faithContext: profile.identity.faithContext,
      weightKg: profile.body.weightKg,
      goalDeltaKcal: profile.training.goalDeltaKcal,
    },
  };
}

// ── TODAY INSIGHTS CONTEXT — full today + yesterday + 7d + recent workouts ──
export function buildTodayContext(allData) {
  const today = todaySlice(allData);
  const yesterday = yesterdaySlice(allData);
  const trend = trend7d(allData);
  const mounjaro = getMounjaroDay(allData.calorieLog);
  const workouts = (allData.workouts || []).slice(0, 5).map(w => ({
    name: w.aiName || w.name || "Workout",
    daysAgo: Math.round((Date.now() - (workoutTs(w) || 0)) / 86400000),
    durationMin: Math.round((w.duration || 0) / 60),
    volume: w.volume || 0,
    sets: w.sets || 0,
    prs: w.prs || 0,
    watch: w.watch ? {
      activityType: w.watch.activityType,
      calories: w.watch.calories,
      avgHr: w.watch.avgHr,
      maxHr: w.watch.maxHr,
    } : null,
  }));
  return {
    today,
    yesterday,
    trend7d: trend,
    recentWorkouts: workouts,
    mounjaro: mounjaro ? { dayOfCycle: mounjaro.dayOfCycle } : null,
  };
}

// ── POST-WORKOUT CONTEXT (HR zones + sleep + today nutrition + macros + prior sessions) ──
export function buildPostWorkoutContext(workout, allData) {
  const todayLog = allData.calorieLog[allData.today] || {};
  const recent = (allData.workouts || []).slice(0, 6);
  const prior = recent.filter(w => (workoutTs(w) || 0) < (workout.timestamp || Date.now())).slice(0, 5);
  return {
    session: {
      name: workout.name,
      durationMin: Math.round((workout.duration || 0) / 60),
      sets: workout.sets || 0,
      volume: workout.volume || 0,
      prs: workout.prs || 0,
      exercises: (workout.exercises || []).map(ex => ({
        name: ex.name,
        muscles: ex.muscles,
        type: ex.type,
        sets: (ex.sets || []).map(s => ({
          weight: parseFloat(s.weight) || 0,
          reps: parseInt(s.reps, 10) || 0,
        })),
      })),
      cardio: workout.cardio || null,
      watch: workout.watch || null,
    },
    today: {
      sleepLastNight: todayLog.sleepMinutes ? {
        totalMin: todayLog.sleepMinutes,
        deepMin: todayLog.deepMinutes || 0,
        remMin: todayLog.remMinutes || 0,
        awakeMin: todayLog.awakeMinutes || 0,
      } : null,
      vitals: {
        restingHr: todayLog.restingHeartRate || null,
        hrv: todayLog.heartRateVariability || null,
      },
      nutritionSoFar: {
        calories: Math.round(calOf(todayLog)),
        protein: Math.round(protOf(todayLog)),
        carbs: Math.round(carbsOf(todayLog)),
        fat: Math.round(fatOf(todayLog)),
      },
      macroGoals: {
        calories: allData.calorieGoals.calories || null,
        protein:  allData.calorieGoals.protein || null,
        carbs:    allData.calorieGoals.carbs || null,
        fat:      allData.calorieGoals.fat || null,
      },
    },
    priorSessions: prior.map(w => ({
      name: w.aiName || w.name,
      daysAgo: Math.round((Date.now() - (workoutTs(w) || 0)) / 86400000),
      durationMin: Math.round((w.duration || 0) / 60),
      sets: w.sets || 0,
      volume: w.volume || 0,
      prs: w.prs || 0,
      avgHr:         w.watch?.avgHr         || null,
      maxHr:         w.watch?.maxHr         || null,
      hrRecoveryBpm: w.watch?.hrRecoveryBpm || null,
      exercises: (w.exercises || []).slice(0, 8).map(ex => ({
        name: ex.name,
        sets: (ex.sets || []).map(s => `${s.weight}×${s.reps}`).join("/"),
      })),
    })),
  };
}

// ── WEEKLY RECAP CONTEXT — per-day curve + cardio + sleep/HRV trend + macro adherence ──
export function buildWeeklyRecapContext(allData) {
  // Mon-start week.
  const weekStart = (() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - (day - 1));
    return d.getTime();
  })();
  const lastWeekStart = weekStart - 7 * 86400000;
  const inRange = (ts, lo, hi) => ts >= lo && ts < hi;
  const thisWeek = (allData.workouts || []).filter(w => inRange(workoutTs(w), weekStart, weekStart + 7 * 86400000));
  const lastWeek = (allData.workouts || []).filter(w => inRange(workoutTs(w), lastWeekStart, weekStart));

  const sumVolume = arr => arr.reduce((t, w) => t + (w.volume || 0), 0);
  const cardioMin = arr => arr.reduce((t, w) => {
    let m = 0;
    if (w.cardio?.duration) m += w.cardio.duration;
    if (w.watch?.durationSec && (w.exercises || []).length === 0) {
      m += Math.round((w.watch.durationSec || 0) / 60);
    }
    return t + m;
  }, 0);

  // Per-day volume curve (Mon → Sun)
  const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const perDay = dayLabels.map(day => ({ day, volume: 0, sessions: 0, prs: 0 }));
  for (const w of thisWeek) {
    const ts = workoutTs(w); if (!ts) continue;
    const d = new Date(ts);
    const idx = ((d.getDay() || 7) - 1) % 7;
    perDay[idx].volume += w.volume || 0;
    perDay[idx].sessions += 1;
    perDay[idx].prs += w.prs || 0;
  }

  // Muscle group hits this week (by set count).
  const muscleHits = {};
  for (const w of thisWeek) {
    for (const ex of (w.exercises || [])) {
      const setCount = (ex.sets || []).length;
      if (setCount === 0) continue;
      const muscles = String(ex.muscles || "").split(",").map(s => s.trim()).filter(Boolean);
      for (const m of muscles) muscleHits[m] = (muscleHits[m] || 0) + setCount;
    }
  }

  // Adherence overlay — last 7 days of calorie log + sleep/HRV.
  const days = Array.from({ length: 7 }, (_, i) => dayOffset(-(6 - i)));
  const adherence = days.map(d => {
    const l = allData.calorieLog[d];
    if (!l) return { date: d, logged: false };
    const cals = Math.round(calOf(l));
    return {
      date: d,
      logged: true,
      calories: cals,
      protein: Math.round(protOf(l)),
      sleepMin: l.sleepMinutes || null,
      hrv: l.heartRateVariability || null,
      restingHr: l.restingHeartRate || null,
      vsCalGoal: allData.calorieGoals.calories ? cals - allData.calorieGoals.calories : null,
    };
  });

  return {
    week: {
      sessions: thisWeek.length,
      volume: Math.round(sumVolume(thisWeek)),
      cardioMin: cardioMin(thisWeek),
      prs: thisWeek.reduce((t, w) => t + (w.prs || 0), 0),
      perDay,
      muscleHits,
    },
    lastWeek: {
      sessions: lastWeek.length,
      volume: Math.round(sumVolume(lastWeek)),
      cardioMin: cardioMin(lastWeek),
    },
    deltas: {
      sessionsDelta: thisWeek.length - lastWeek.length,
      volumeDelta: Math.round(sumVolume(thisWeek) - sumVolume(lastWeek)),
    },
    adherence,
  };
}

// ── COACH REPORT CONTEXT (range payload + sleep/HR/steps/workouts/fasting/profile) ──
export function buildCoachReportContext(allData, rangeDays) {
  const days = Array.from({ length: rangeDays }, (_, i) => dayOffset(-(rangeDays - 1 - i)));
  const dailyLog = days.map(d => {
    const l = allData.calorieLog[d];
    if (!l) return null;
    const cal = Math.round(calOf(l));
    if (cal === 0 && !l.sleepMinutes && !l.steps) return null;
    return {
      date: d,
      calories: cal,
      protein: Math.round(protOf(l)),
      carbs: Math.round(carbsOf(l)),
      fat: Math.round(fatOf(l)),
      fiber: Math.round(fiberOf(l)),
      water: l.water || 0,
      caloriesBurned: Math.round((l.caloriesBurned || 0) + (l.basalBurned || 0)),
      sleepMin: l.sleepMinutes || null,
      deepMin: l.deepMinutes || null,
      remMin: l.remMinutes || null,
      steps: l.steps || null,
      exerciseMin: l.exerciseMinutes || null,
      restingHr: l.restingHeartRate || null,
      hrv: l.heartRateVariability || null,
      bodyMassKg: l.bodyMassKg || null,
      bodyFatPct: l.bodyFatPct || null,
    };
  }).filter(Boolean);

  // Workouts in range.
  const sinceTs = Date.now() - rangeDays * 86400000;
  const workouts = (allData.workouts || [])
    .filter(w => (workoutTs(w) || 0) >= sinceTs)
    .map(w => ({
      name: w.aiName || w.name,
      daysAgo: Math.round((Date.now() - workoutTs(w)) / 86400000),
      durationMin: Math.round((w.duration || 0) / 60),
      sets: w.sets || 0,
      volume: w.volume || 0,
      prs: w.prs || 0,
      watch: w.watch ? { activityType: w.watch.activityType, calories: w.watch.calories } : null,
    }));

  const weights = (allData.weightLog || []).slice(-14).map(w => ({ ts: w.ts, kg: w.kg }));
  const profile  = getUserProfile();
  const mounjaro = getMounjaroDay(allData.calorieLog);

  return {
    rangeDays,
    profile: {
      region: profile.identity.region,
      bio: profile.identity.bio,
      sex: profile.body.sex,
      age: profile.body.age,
      heightCm: profile.body.heightCm,
      weightKg: profile.body.weightKg,
      bmr: profile.body.bmr,
      tdee: profile.body.tdee,
      activityLevel: profile.training.activityLabel,
      goalDeltaKcal: profile.training.goalDeltaKcal,
      caloriesGoal: profile.nutrition.caloriesGoal,
      proteinGoal: profile.nutrition.proteinGoal,
      carbsGoal: profile.nutrition.carbsGoal,
      fatGoal: profile.nutrition.fatGoal,
      weightTarget: profile.nutrition.weightTarget,
      fastingProtocol: profile.preferences.fastingProtocol,
    },
    dailyLog,
    workouts,
    weights,
    mounjaro: mounjaro ? { dayOfCycle: mounjaro.dayOfCycle } : null,
  };
}

// ── PATTERN INSIGHTS CONTEXT — per-day rows + persona + workout flags ──
export function buildPatternInsightsContext(allData, rangeDays) {
  const days = Array.from({ length: rangeDays }, (_, i) => dayOffset(-(rangeDays - 1 - i)));
  const workoutDaySet = new Set();
  for (const w of (allData.workouts || [])) {
    const ts = workoutTs(w); if (!ts) continue;
    const d = new Date(ts);
    workoutDaySet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  }
  const dailyLog = days.map(d => {
    const l = allData.calorieLog[d];
    if (!l) return null;
    const cal = Math.round(calOf(l));
    if (cal === 0 && !l.sleepMinutes && !l.steps) return null;
    const dow = new Date(d).getDay();
    return {
      date: d,
      isWeekend: dow === 0 || dow === 6,
      isWorkout: workoutDaySet.has(d),
      calories: cal,
      protein: Math.round(protOf(l)),
      carbs: Math.round(carbsOf(l)),
      fat: Math.round(fatOf(l)),
      junkCalories: Math.round((l.meals?.snacks || []).reduce((s, e) => s + (e.calories || 0), 0)),
      sleepMin: l.sleepMinutes || null,
      steps: l.steps || null,
      bodyMassKg: l.bodyMassKg || null,
    };
  }).filter(Boolean);
  const profile  = getUserProfile();
  const mounjaro = getMounjaroDay(allData.calorieLog);
  return {
    rangeDays,
    profile: {
      region: profile.identity.region,
      bio: profile.identity.bio,
      faithContext: profile.identity.faithContext,
      caloriesGoal: profile.nutrition.caloriesGoal,
      proteinGoal: profile.nutrition.proteinGoal,
      weightTarget: profile.nutrition.weightTarget,
      goalDeltaKcal: profile.training.goalDeltaKcal,
    },
    mounjaro: mounjaro ? { dayOfCycle: mounjaro.dayOfCycle } : null,
    dailyLog,
    aggregates: {
      daysLogged: dailyLog.length,
      avgCalories: avg(dailyLog.map(d => d.calories)),
      avgProtein: avg(dailyLog.map(d => d.protein)),
      avgSleepMin: avg(dailyLog.map(d => d.sleepMin)),
      avgSteps: avg(dailyLog.map(d => d.steps)),
      workoutDayAvgCal: avg(dailyLog.filter(d => d.isWorkout).map(d => d.calories)),
      restDayAvgCal: avg(dailyLog.filter(d => !d.isWorkout).map(d => d.calories)),
      weekendAvgCal: avg(dailyLog.filter(d => d.isWeekend).map(d => d.calories)),
      weekdayAvgCal: avg(dailyLog.filter(d => !d.isWeekend).map(d => d.calories)),
      junkDays: dailyLog.filter(d => d.junkCalories > 0).length,
      junkCalAvg: avg(dailyLog.map(d => d.junkCalories)),
    },
  };
}

// ── AI COACH CONTEXT — relevance-classified subsets ─────────────────────
// Lightweight keyword classifier. Picks which sections to include based
// on what the user just asked. Multiple categories can match. Cheap —
// no extra LLM call.
const SECTION_KEYWORDS = {
  exercise:    /\b(workout|exercise|train|training|gym|set|reps|weight|lift|squat|bench|deadlift|cardio|riyadah|volume|pr|hypertrophy|strength|recover|rest day|push|pull|legs|muscle|bicep|tricep|chest|back|shoulder|core|pump)\b/i,
  nutrition:   /\b(eat|ate|food|meal|protein|carb|fat|fiber|sugar|sodium|calorie|kcal|kj|macros?|deficit|surplus|cut|bulk|recomp|whey|supplement|fasted|broke fast|fed|hungry|biriyani|chicken|rice|date|dates|sunnah)\b/i,
  recovery:    /\b(sleep|sleep[d-]|deep|rem|awake|nap|rest|recovery|tired|fatigue|wake|bed|sore|hrv|resting heart|rhr|stress)\b/i,
  composition: /\b(weight|kg|lbs|pounds?|body fat|bf%|body comp|recomp|lean mass|muscle mass|skinny|fat|leaner|gain|lose|cut|bulk)\b/i,
  fasting:     /\b(fast|fasting|fasted|broke fast|break fast|sunnah fast|monday|thursday|ramadan|sahoor|suhoor|iftar|window|autophagy|ketones?|16:8|18:6|omad)\b/i,
  today:       /\b(today|now|right now|this morning|current|so far)\b/i,
};

function relevantSections(_query) {
  // Always include every section the user has granted access to. The earlier
  // keyword-based gate was too brittle: "session summary across the
  // conversation" returned only ["today"], starving the AI of exercise data
  // even when the user later asked workout questions in the same session.
  // Per-section gating still happens downstream via `access.{calories,workout,
  // sleep,weight,bf,steps,fasting}` — so the user's consent controls what
  // leaves the device. Token cost of including everything is negligible
  // against Gemini Flash's 1M-token window.
  return ["today", "nutrition", "recovery", "composition", "exercise", "fasting"];
}

// Builds the per-turn user-message preamble for AI Coach. Includes
// only the data slices that look relevant to the query, additionally
// gated by `access.{calories,weight,bf,sleep,steps,workout,fasting}`.
//
// Returns a JSON string (compact format) — the chat path sends this
// as a text part prepended to the user's actual message.
// Every distinct food the user has EVER logged — name, how often, average
// calories, and when last eaten — most-eaten first. Gives the Coach access to
// the user's whole eating history (not just today) so it can analyse patterns,
// recall what they "usually" eat, and answer "what have I been eating?".
function eatingHistory(allData, maxDistinct = 60) {
  const log = allData.calorieLog || {};
  const byName = {};
  const dates = Object.keys(log).sort(); // ascending → last write wins for lastEaten
  for (const date of dates) {
    const meals = (log[date] && log[date].meals) || {};
    for (const arr of Object.values(meals)) {
      for (const e of (arr || [])) {
        if (!e || !e.name) continue;
        const k = String(e.name).trim();
        const r = byName[k] || { name: k, count: 0, kcalSum: 0, protSum: 0, lastDate: date };
        r.count += 1;
        r.kcalSum += (e.calories || 0);
        r.protSum += (e.protein || 0);
        r.lastDate = date;
        byName[k] = r;
      }
    }
  }
  return Object.values(byName)
    .map(r => ({
      name: r.name,
      timesEaten: r.count,
      avgKcal: Math.round(r.kcalSum / r.count),
      avgProtein: Math.round(r.protSum / r.count),
      lastEaten: r.lastDate,
    }))
    .sort((a, b) => (b.timesEaten - a.timesEaten) || (a.lastEaten < b.lastEaten ? 1 : -1))
    .slice(0, maxDistinct);
}

export function buildCoachContext(query, allData, access) {
  access = access || {};
  const sections = relevantSections(query);
  const out = {};

  if (sections.includes("today")) {
    const t = todaySlice(allData);
    const compact = {
      date: t.date,
      foods: t.foods,
      foodsNote: t.foods.length > 0
        ? `These ${t.foods.length} items are the ONLY foods logged today. Use exact names — do not rename, merge, or invent items.`
        : "No individual foods logged today. Do not invent a food list. intake totals may reflect prior context only.",
      intake: t.intake,
      goals: t.goals,
      remaining: t.remaining,
    };
    if (access.steps) compact.activity = t.activity;
    if (access.sleep && t.sleepLastNight) compact.sleep = t.sleepLastNight;
    if (access.sleep) compact.vitals = t.vitals;
    if (access.weight && t.body.weightKg) compact.weightKg = t.body.weightKg;
    if (access.bf && t.body.bodyFatPct) compact.bodyFatPct = t.body.bodyFatPct;
    out.today = compact;
  }

  // Junk tracker — always include when calories access is granted. The
  // user's biggest stated weakness is junk food, so the Coach should be
  // able to weigh in even on questions that didn't keyword-match
  // "nutrition" (e.g. "should I have a snack now?"). Cheap to compute.
  if (access.calories) {
    // Whole eating history (all-time distinct foods) so the Coach can analyse
    // beyond today — "what do I usually eat", patterns, repeated items.
    out.eatingHistory = {
      note: "Every distinct food you've logged (all-time), most-eaten first. Use to recall what the user usually eats and spot patterns.",
      distinctFoods: eatingHistory(allData),
    };
    const claimed = isCheatClaimedToday(allData.today);
    const stats30 = computeRangeJunkStats(allData.calorieLog, 30, DEFAULT_JUNK_THRESHOLD);
    const stats7  = computeRangeJunkStats(allData.calorieLog, 7,  DEFAULT_JUNK_THRESHOLD);
    const streak  = computeJunkStreak(allData.calorieLog, DEFAULT_JUNK_THRESHOLD, allData.today);
    const earned  = computeTodayEarnedCheat(allData.calorieLog, DEFAULT_JUNK_THRESHOLD, allData.today, claimed);
    out.junkTracker = {
      threshold: DEFAULT_JUNK_THRESHOLD,
      cleanDayDefinition: `junk_kcal / total_kcal < ${DEFAULT_JUNK_THRESHOLD}%`,
      today: {
        pct: stats30.series[stats30.series.length - 1]?.pct ?? 0,
        junkKcal: stats30.series[stats30.series.length - 1]?.junkKcal ?? 0,
        totalKcal: stats30.series[stats30.series.length - 1]?.totalKcal ?? 0,
        isClean: stats30.series[stats30.series.length - 1]?.isClean ?? false,
      },
      streak: { current: streak.current, best: streak.best, freezeUsedRecently: streak.freezeUsedRecently },
      cheatMeal: {
        eligibleToday: earned.eligible,
        claimedToday: claimed,
        suggestedCheatKcal: earned.suggestedKcal,
        reason: earned.reason,
      },
      last7d: {
        avgPct: stats7.avgPct,
        cleanDays: stats7.cleanDays,
        loggedDays: stats7.loggedDays,
      },
      last30d: {
        avgPct: stats30.avgPct,
        cleanDays: stats30.cleanDays,
        loggedDays: stats30.loggedDays,
        weekOverWeekDelta: stats30.wow.delta,   // negative = improving
        byWeek: stats30.byWeek.map(w => ({ weekStart: w.weekStart, pct: w.pct, cleanDays: w.cleanDays })),
        byWeekday: stats30.byWeekday,
        topByKcal: stats30.topByKcal,
        topByFreq: stats30.topByFreq,
      },
    };
  }

  if (sections.includes("nutrition") && access.calories) {
    const days = Array.from({ length: 7 }, (_, i) => dayOffset(-(6 - i)));
    const series = days.map(d => {
      const l = allData.calorieLog[d]; if (!l) return null;
      const cal = Math.round(calOf(l));
      if (cal === 0) return null;
      return { date: d, calories: cal, protein: Math.round(protOf(l)) };
    }).filter(Boolean);
    out.nutrition7d = {
      days: series,
      avgCal: avg(series.map(s => s.calories)),
      avgProt: avg(series.map(s => s.protein)),
    };
    const todayLog = allData.calorieLog[allData.today] || {};
    const items = [];
    for (const [slot, arr] of Object.entries(todayLog.meals || {})) {
      if (!Array.isArray(arr)) continue;
      for (const m of arr) {
        items.push({
          slot,
          name: m.name,
          cal: Math.round(m.calories || 0),
          prot: Math.round(m.protein || 0),
        });
      }
    }
    if (items.length) {
      out.todayMeals = items;
      out.todayMealsNote = `${items.length} items — authoritative list. Use exact names.`;
    }

    // ── 30-DAY DEEP NUTRITION CONTEXT ────────────────────────────────────
    // The Coach can answer "trends in my junk over the last 30 days",
    // "what should I cut", "which day of the week am I worst" etc only
    // when it actually sees the full 30-day meal stream. Per-slot
    // aggregates per day + day-of-week summary + top-junk ranking are
    // all derived here so the model doesn't have to compute them itself.
    const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const days30 = Array.from({ length: 30 }, (_, i) => dayOffset(-(29 - i)));
    const slot30 = []; // per-day per-slot kcal + macros
    const dowAgg = {}; // day-of-week → totals
    const junkTotals = new Map(); // food name → { count, totalCal }
    const drinkTotals = new Map();
    let totalCal30 = 0, totalJunkCal30 = 0, totalDrinksCal30 = 0;
    let totalProt30 = 0, totalCarbs30 = 0, totalFat30 = 0, totalSugar30 = 0;
    let daysLogged = 0;


    for (const d of days30) {
      const l = allData.calorieLog[d];
      if (!l || !l.meals) continue;
      const meals = l.meals;
      const weekday = dow[new Date(`${d}T12:00:00`).getDay()];
      const dayBucket = { date: d, weekday, slots: {} };
      let dayCal = 0, dayProt = 0, dayCarbs = 0, dayFat = 0, daySugar = 0;
      for (const [slot, arr] of Object.entries(meals)) {
        if (!Array.isArray(arr) || !arr.length) continue;
        let cal = 0, prot = 0, carbs = 0, fat = 0, sugar = 0;
        const slotItems = []; // individual items so the AI can name them
        for (const m of arr) {
          const mc = +m?.calories || 0;
          const mp = +m?.protein  || 0;
          const mcb = +m?.carbs   || 0;
          const mf = +m?.fat      || 0;
          const ms = +m?.sugar    || 0;
          cal += mc; prot += mp; carbs += mcb; fat += mf; sugar += ms;
          const itemName = (m?.name || 'Unknown').trim();
          const compactItem = {
            name: itemName,
            cal: Math.round(mc),
            prot: Math.round(mp),
            carbs: Math.round(mcb),
            fat: Math.round(mf),
            sugar: Math.round(ms),
          };
          slotItems.push(compactItem);
          // Build per-slot frequency tables so the substitution coach can
          // call out the actual top offenders by name.
          const map = slot === 'snacks' ? junkTotals : slot === 'drinks' ? drinkTotals : null;
          if (map) {
            const prev = map.get(itemName) || { count: 0, totalCal: 0 };
            map.set(itemName, { count: prev.count + 1, totalCal: prev.totalCal + mc });
          }
        }
        dayBucket.slots[slot] = {
          cal: Math.round(cal), prot: Math.round(prot), carbs: Math.round(carbs), fat: Math.round(fat), sugar: Math.round(sugar),
          items: slotItems,
        };
        dayCal += cal; dayProt += prot; dayCarbs += carbs; dayFat += fat; daySugar += sugar;
        if (slot === 'snacks') totalJunkCal30 += cal;
        if (slot === 'drinks') totalDrinksCal30 += cal;
      }
      dayBucket.totalCal = Math.round(dayCal);
      if (dayCal > 0) {
        slot30.push(dayBucket);
        totalCal30 += dayCal; totalProt30 += dayProt; totalCarbs30 += dayCarbs; totalFat30 += dayFat; totalSugar30 += daySugar;
        daysLogged++;
        const w = dayBucket.weekday;
        if (!dowAgg[w]) dowAgg[w] = { count: 0, totalCal: 0, totalJunkCal: 0, totalDrinksCal: 0 };
        dowAgg[w].count++;
        dowAgg[w].totalCal     += dayCal;
        dowAgg[w].totalJunkCal += (dayBucket.slots.snacks?.cal || 0);
        dowAgg[w].totalDrinksCal += (dayBucket.slots.drinks?.cal || 0);
      }
    }
    // Convert dow aggregates to per-day averages.
    const dayOfWeekAvg = {};
    for (const [w, agg] of Object.entries(dowAgg)) {
      dayOfWeekAvg[w] = {
        daysLogged: agg.count,
        avgCal:     Math.round(agg.totalCal / agg.count),
        avgJunkCal: Math.round(agg.totalJunkCal / agg.count),
        avgDrinksCal: Math.round(agg.totalDrinksCal / agg.count),
      };
    }
    // Top junk + drinks items by total kcal across 30 days.
    const toTop = (m) => [...m.entries()]
      .map(([name, v]) => ({ name, count: v.count, totalCal: Math.round(v.totalCal), avgCal: Math.round(v.totalCal / v.count) }))
      .sort((a, b) => b.totalCal - a.totalCal)
      .slice(0, 15);
    const topJunkItems30d = toTop(junkTotals);
    const topDrinkItems30d = toTop(drinkTotals);

    out.nutrition30d = {
      daysLogged,
      totalCal:     Math.round(totalCal30),
      avgCal:       daysLogged ? Math.round(totalCal30 / daysLogged) : 0,
      avgProtein:   daysLogged ? Math.round(totalProt30 / daysLogged) : 0,
      avgCarbs:     daysLogged ? Math.round(totalCarbs30 / daysLogged) : 0,
      avgFat:       daysLogged ? Math.round(totalFat30 / daysLogged) : 0,
      avgSugar:     daysLogged ? Math.round(totalSugar30 / daysLogged) : 0,
      totalJunkCal: Math.round(totalJunkCal30),
      avgJunkCal:   daysLogged ? Math.round(totalJunkCal30 / daysLogged) : 0,
      totalDrinksCal: Math.round(totalDrinksCal30),
      avgDrinksCal: daysLogged ? Math.round(totalDrinksCal30 / daysLogged) : 0,
      pctJunk: totalCal30 > 0 ? +(totalJunkCal30 / totalCal30 * 100).toFixed(1) : 0,
      days: slot30,           // per-day per-slot breakdown WITH per-item arrays
      dayOfWeekAvg,           // Mon/Tue/Wed/... averages
      topJunkItems: topJunkItems30d,
      topDrinkItems: topDrinkItems30d,
    };
  }

  if (sections.includes("recovery") && access.sleep) {
    const days = Array.from({ length: 7 }, (_, i) => dayOffset(-(6 - i)));
    const series = days.map(d => {
      const l = allData.calorieLog[d]; if (!l) return null;
      return {
        date: d,
        sleepMin: l.sleepMinutes || null,
        deepMin: l.deepMinutes || null,
        remMin: l.remMinutes || null,
        restingHr: l.restingHeartRate || null,
        hrv: l.heartRateVariability || null,
      };
    }).filter(Boolean);
    out.recovery7d = {
      days: series,
      avgSleepMin: avg(series.map(s => s.sleepMin)),
      avgHrv: avg(series.map(s => s.hrv)),
      avgRestingHr: avg(series.map(s => s.restingHr)),
    };
  }

  if (sections.includes("composition") && (access.weight || access.bf)) {
    const wl = (allData.weightLog || []).slice(-14);
    out.composition = {
      recentWeights: wl,
      latestWeightKg: wl.length ? wl[wl.length - 1].kg : null,
      target: allData.calorieGoals.weight || null,
    };
  }

  if (sections.includes("exercise") && access.workout) {
    const sorted = [...(allData.workouts || [])]
      .sort((a, b) => workoutTs(b) - workoutTs(a))
      .slice(0, 8);
    // Per-workout payload — last 5 keep FULL exercise + sets/reps/weights so
    // the coach can give specific feedback ("your bench dropped from 80x6 to
    // 75x6 — deload week?"). Older sessions stay aggregate-only to keep
    // context tight.
    const detail = (w) => {
      const exercises = (w.exercises || []).map(ex => ({
        name: ex.name,
        muscles: ex.muscles || null,
        sets: (ex.sets || []).map(s => ({
          weightKg: parseFloat(s.weight) || 0,
          reps: parseInt(s.reps, 10) || 0,
        })),
      }));
      // Logging mode: watch-only entries come from Apple Watch native workouts
      // (Apple Fitness app) and carry only duration/HR/calories — no sets/reps.
      // Riyadah Active Session entries carry full per-exercise sets[].
      const loggedVia = w.watchOnly === true
        ? "apple_watch_native"
        : (exercises.length > 0 ? "riyadah_session" : "imported_summary");
      return {
        name: w.aiName || w.name,
        date: (() => { try { return new Date(workoutTs(w)).toISOString().slice(0,10); } catch { return null; } })(),
        daysAgo: Math.round((Date.now() - workoutTs(w)) / 86400000),
        durationMin: Math.round((w.duration || 0) / 60),
        sets: w.sets || 0,
        volume: w.volume || 0,
        prs: w.prs || 0,
        loggedVia,
        muscles: Array.from(new Set(
          (w.exercises || [])
            .flatMap(ex => String(ex.muscles || "").split(",").map(s => s.trim()).filter(Boolean))
        )).slice(0, 5),
        exercises,
        cardio: w.cardio ? {
          type: w.cardio.type || w.cardio.activityType || null,
          durationMin: Math.round((w.cardio.duration || 0) / 60),
          distanceKm: w.cardio.distanceKm || null,
          calories: w.cardio.calories || null,
        } : null,
        watch: w.watch ? {
          activityType: w.watch.activityType || null,
          calories: w.watch.calories || null,
          avgHr: w.watch.avgHr || null,
          maxHr: w.watch.maxHr || null,
          zones: w.watch.zones || w.watch.hrZoneSeconds || null,
        } : null,
      };
    };
    const summary = (w) => ({
      name: w.aiName || w.name,
      daysAgo: Math.round((Date.now() - workoutTs(w)) / 86400000),
      durationMin: Math.round((w.duration || 0) / 60),
      sets: w.sets || 0,
      volume: w.volume || 0,
      prs: w.prs || 0,
    });
    const allWorkouts = allData.workouts || [];
    const richSessionsAllTime = allWorkouts.filter(w => (w.exercises || []).some(ex => (ex.sets || []).length > 0)).length;
    const richSessions30d = allWorkouts.filter(w => {
      if (!(w.exercises || []).some(ex => (ex.sets || []).length > 0)) return false;
      return (Date.now() - workoutTs(w)) < 30 * 86400000;
    }).length;

    // FLAT cross-session list of exercise instances, newest first. Caps at 30
    // so the AI can answer "give me a table of my last 15 exercises" or
    // "which exercises did I do this week" without needing to re-derive from
    // the per-workout tree. Each entry carries the workout date so the AI
    // can group by session if asked.
    const flat = [];
    for (const w of sorted) {
      const dateISO = (() => { try { return new Date(workoutTs(w)).toISOString().slice(0,10); } catch { return null; } })();
      const daysAgo = Math.round((Date.now() - workoutTs(w)) / 86400000);
      for (const ex of (w.exercises || [])) {
        const sets = (ex.sets || []).map(s => ({
          weightKg: parseFloat(s.weight) || 0,
          reps: parseInt(s.reps, 10) || 0,
        })).filter(s => s.reps > 0 || s.weightKg > 0);
        if (sets.length === 0) continue;
        const topSet = sets.reduce((b, s) => ((s.weightKg * s.reps) > (b.weightKg * b.reps) ? s : b), sets[0]);
        // Use the canonical (library-preferred) name so the Coach sees
        // one stable identifier per exercise, even when historical logs
        // used variant spellings (Steve sends "Dumbbell Goblet Squats",
        // the library calls it "DB Goblet Squat").
        const canonName = resolveExerciseName(ex.name, ex.id);
        flat.push({
          exercise: canonName,
          aliasName: ex.name !== canonName ? ex.name : undefined,
          muscles: ex.muscles || null,
          date: dateISO,
          daysAgo,
          workout: w.aiName || w.name,
          setsCount: sets.length,
          totalReps: sets.reduce((t, s) => t + s.reps, 0),
          volumeKg: Math.round(sets.reduce((t, s) => t + s.weightKg * s.reps, 0)),
          topSet,
          sets,
        });
        if (flat.length >= 30) break;
      }
      if (flat.length >= 30) break;
    }

    // PERSONAL BESTS — best weight per exercise across the entire history,
    // with the workout it happened in. Uses weightKg as the PR metric (the
    // canonical strength PR — more weight = harder). Ties broken by reps.
    const bests = new Map();
    for (const w of allWorkouts) {
      const dateISO = (() => { try { return new Date(workoutTs(w)).toISOString().slice(0,10); } catch { return null; } })();
      for (const ex of (w.exercises || [])) {
        // PR map keyed by CANONICAL id, not name — so "Goblet Squats"
        // and "DB Goblet Squat" accumulate into one PR entry instead of
        // splitting (which made the Coach answer "no PRs for that lift"
        // when the user had clearly hit several).
        const canonId   = resolveExerciseId(ex.name, ex.id);
        const canonName = resolveExerciseName(ex.name, ex.id);
        for (const s of (ex.sets || [])) {
          const wt = parseFloat(s.weight) || 0;
          const rp = parseInt(s.reps, 10) || 0;
          if (wt <= 0 || rp <= 0) continue;
          const existing = bests.get(canonId);
          if (!existing || wt > existing.weightKg || (wt === existing.weightKg && rp > existing.reps)) {
            bests.set(canonId, {
              exercise: canonName,
              weightKg: wt,
              reps: rp,
              date: dateISO,
              workout: w.aiName || w.name,
            });
          }
        }
      }
    }
    const personalBests = Array.from(bests.values()).sort((a, b) => b.weightKg - a.weightKg);

    out.exercise = {
      // Per-workout view — first 10 with FULL exercises/sets so the AI can
      // analyse session structure, last-session-vs-this-session comparisons,
      // PR moments etc.
      recent: sorted.slice(0, 10).map(detail),
      // Older sessions stay aggregate-only.
      older: sorted.slice(10).map(summary),
      // FLAT exercise list answering "last N exercises" queries directly.
      lastExercises: flat,
      // PR table answering "what PRs have I hit" queries directly.
      personalBests,
      thisWeekCount: allWorkouts.filter(w => (Date.now() - workoutTs(w)) < 7 * 86400000).length,
      richSessions30d,
      richSessionsAllTime,
    };
    // Cardiovascular-fitness trend (RHR + walking HR + HR recovery + VO₂max)
    // so the Coach can answer "is my conditioning improving?" with direction,
    // not just today's snapshot.
    try {
      const cf = computeCardioFitness();
      if (cf.hasData) out.cardioFitness = { verdict: cf.verdict, window: cf.windowLabel, signals: cf.signals, summary: cf.summary };
    } catch {}
  }

  if (sections.includes("fasting") && access.fasting) {
    // Source of truth for daily fasting is `calorieLog` meal timestamps,
    // not `hayahFastEvents` (which only captures Gemini-generated insights
    // for fasts > 9h that triggered the post-fast toast). Compute the
    // actual fasting window for every day with meal data in the last 30
    // days so the AI sees the granular reality — fasts that fell short of
    // 9h, in-progress fasts today, overrides, the lot.
    const prefs = allData.prefs || {};
    const fastingPrefs = prefs.fasting || {};
    const protocol = fastingPrefs.protocol || "16:8";
    const protoCfg = FASTING_PROTOCOLS[protocol] || FASTING_PROTOCOLS["16:8"];
    const goalHours = Number.isFinite(fastingPrefs.goalHours) ? fastingPrefs.goalHours : protoCfg.goalHours;
    const goodHours = Number.isFinite(fastingPrefs.goodHours) ? fastingPrefs.goodHours : protoCfg.goodHours;
    const okHours   = Number.isFinite(fastingPrefs.okHours)   ? fastingPrefs.okHours   : protoCfg.okHours;
    const fastSettings = {
      tolerance: fastingPrefs.tolerance || 0,
      allowList: fastingPrefs.allowList || [],
      bedtimeOffsetMin: fastingPrefs.bedtimeOffsetMin,
    };

    const overrides = allData.fastingOverrides || {};
    const days30 = Array.from({ length: 30 }, (_, i) => dayOffset(-(29 - i)));
    const dailyFasts = [];
    for (const d of days30) {
      const w = computeFastingWindow(allData.calorieLog, d, overrides, fastSettings);
      if (!w || w.hours == null) continue;
      // First non-tolerated meal of the day = the breaking meal.
      let breakingMeal = null;
      const todayEntry = allData.calorieLog[d];
      if (todayEntry && w.endTs && !w.inProgress) {
        const flat = [];
        for (const slot of Object.keys(todayEntry.meals || {})) {
          const arr = Array.isArray(todayEntry.meals[slot]) ? todayEntry.meals[slot] : [];
          for (const m of arr) flat.push({ ...m, slot });
        }
        const tsOf = (m) => {
          const t = m.loggedAt ? Date.parse(m.loggedAt) : (m.ts || NaN);
          return Number.isFinite(t) ? t : NaN;
        };
        const sorted = flat.map(m => ({ m, t: tsOf(m) }))
          .filter(x => Number.isFinite(x.t))
          .sort((a, b) => a.t - b.t);
        const tEnd = w.endTs;
        const match = sorted.find(x => Math.abs(x.t - tEnd) < 60 * 1000) || sorted[0];
        if (match) breakingMeal = { name: match.m.name, slot: match.m.slot, cal: Math.round(match.m.calories || 0) };
      }
      dailyFasts.push({
        date: d,
        hours: +w.hours.toFixed(2),
        source: w.source,             // "lastMeal" | "manual" | "bedtime+offset"
        inProgress: !!w.inProgress,
        hitGoal: w.hours >= goalHours,
        hitGood: w.hours >= goodHours,
        hitOk:   w.hours >= okHours,
        breakingMeal,
        note: w.note || null,
        overridden: !!w.override,
      });
    }

    const completed = dailyFasts.filter(f => !f.inProgress);
    const hoursList = completed.map(f => f.hours);

    out.fasting = {
      protocol,
      thresholds: { okHours, goodHours, goalHours },
      settings: {
        tolerance: fastSettings.tolerance,
        allowList: fastSettings.allowList,
      },
      // Granular daily window for the last 30 days — primary source.
      dailyFasts30d: dailyFasts,
      // Aggregates so the AI doesn't have to recompute.
      stats30d: {
        daysLogged: completed.length,
        avgHours: avg(hoursList),
        minHours: hoursList.length ? +Math.min(...hoursList).toFixed(2) : null,
        maxHours: hoursList.length ? +Math.max(...hoursList).toFixed(2) : null,
        daysHittingGoal: completed.filter(f => f.hitGoal).length,
        daysHittingGood: completed.filter(f => f.hitGood).length,
        daysHittingOk:   completed.filter(f => f.hitOk).length,
        daysUnderOk:     completed.filter(f => !f.hitOk).length,
      },
      // Gemini-generated post-fast insights (separate from raw daily data).
      recentInsights: (allData.fastEvents || []).slice(0, 10).map(e => ({
        endTs: e.end_ts,
        durationMin: e.duration_min,
        tiers: e.tiers_reached || [],
        breakingMealName: e.breaking_meal?.name || null,
        insight: e.insight || null,
      })),
    };

    // LIVE current state — what the user actually wants the coach to reason
    // about right now: "you're 4h since lunch; eating dinner in the next 2h
    // locks a 16h fast tomorrow". Two cases:
    //   1. completed fast (yesterday → first meal today) — already in
    //      dailyFasts30d above as the last entry; surface its key fields.
    //   2. since-last-meal today — `computeCurrentMealWindow` knows this.
    const todayStr = dayOffset(0);
    const cur = computeCurrentMealWindow(allData.calorieLog, todayStr, prefs);
    const todayFast = computeFastingWindow(allData.calorieLog, todayStr, overrides, fastSettings);
    out.fasting.live = {
      now: new Date().toISOString(),
      mode: cur.mode,                                 // "fasting" | "eating"
      mealsTodayCount: cur.mealsTodayCount,
      lastMealName:    cur.lastMealName,
      lastMealSlot:    cur.lastMealSlot,
      lastMealTs:      cur.lastMealTs,
      hoursSinceLastMeal: cur.hoursSinceLastMeal != null ? +cur.hoursSinceLastMeal.toFixed(2) : null,
      stage:           cur.stage?.slug || null,
      stageLabel:      cur.stage?.label || null,
      nextStage:       cur.nextStage?.slug || null,
      hoursToNextStage: cur.hoursToNextStage != null ? +cur.hoursToNextStage.toFixed(2) : null,
      eatingWindow:    cur.eatingWindow,
      completedFastToday: todayFast?.hours != null && !todayFast.inProgress ? {
        startTs: todayFast.startTs,
        endTs:   todayFast.endTs,
        hours:   +todayFast.hours.toFixed(2),
        source:  todayFast.source,
        hitGoal: todayFast.hours >= goalHours,
      } : null,
      // Hours-since-last-meal IS the live ongoing fast when no eating
      // cutoff is set and the user wants to keep fasting from here. We
      // surface it explicitly so the Coach can answer "how long have I
      // been fasting?" the way the user means it.
      ongoingFastHours: cur.hoursSinceLastMeal != null ? +cur.hoursSinceLastMeal.toFixed(2) : null,
    };
  }

  // Mounjaro: always include when logged in the last 14 days — relevant to
  // any performance / nutrition question and the system prompt already explains
  // the cycle effect. No access gate: it's non-sensitive.
  const mounjaro = getMounjaroDay(allData.calorieLog);
  if (mounjaro) {
    out.mounjaro = { dayOfCycle: mounjaro.dayOfCycle };
  }

  // Hayah Settings snapshot — non-sensitive user preferences so the Coach can
  // ground recommendations in the user's *configured* goals/protocol/units
  // rather than guessing defaults. Mirrors what's editable in
  // Hayah → Settings.
  const prefsSnap = allData.prefs || {};
  out.settings = {
    sleep:    prefsSnap.sleep    || null,
    activity: prefsSnap.activity || null,
    exercise: prefsSnap.exercise || null,
    fasting:  prefsSnap.fasting  || null,
    calories: prefsSnap.calories || null,
    units:    prefsSnap.units    || null,
    macroGoals: allData.calorieGoals || null,
  };

  return JSON.stringify(out);
}
